import { isNull, omitBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

export type BaseFilterShape = {
  search?: string;
};

export enum ChangeTypes {
  Debounce = 'debounce',
}

export const useFilters = <T extends BaseFilterShape>(
  initialState: T = {} as T
) => {
  const [filters, setFilters] = useState<T>(initialState);

  const [searchValue, setSearchValue] = useState(filters.search);
  const [debouncedSearchValue] = useDebounce(searchValue, 300);

  useEffect(() => {
    if (debouncedSearchValue !== filters.search) {
      updateFilters({ search: debouncedSearchValue } as Partial<T>);
    }
  }, [debouncedSearchValue]);

  const updateFilters = useCallback((inputs: Partial<T>) => {
    setFilters((prev) => {
      const nextFilters = { ...prev, ...inputs };
      return omitBy(nextFilters, (value) => value === '' || isNull(value)) as T;
    });
  }, []);

  const filtersChange = useCallback(
    (inputs: Partial<T>, type?: ChangeTypes) => {
      if (type === ChangeTypes.Debounce) {
        setSearchValue(inputs.search || '');

        return;
      }

      updateFilters(inputs);
    },
    [updateFilters]
  );

  return {
    filters,
    filtersChange,
  };
};
